/**
 * v132 — Range Log v3 schema additions.
 *
 * Adds the concept of a SET inside a session. A set is a contiguous group of
 * shots at one configuration. Advanced tuners shoot a group → tweak something
 * → shoot another group → compare. v3 makes that loop first-class.
 *
 * Also locks the color palette used by the Compare Overlay so the same set
 * always renders in the same color across views.
 *
 * Backward compatibility
 * ──────────────────────
 * `ShotSession.sets?` is optional. Sessions created in v130/v131 keep working
 * with flat `richShots[]`. At read time, `migrateSessionToV3()` wraps any flat
 * `richShots[]` in a single implicit Set so the UI sees a consistent shape.
 * On the next write we persist the v3 shape; we do NOT bulk-rewrite history
 * on first load (low-risk for data integrity).
 */

import type { RichShot } from './v2-schema';

/** A set of shots fired under one configuration. */
export interface ShotSet {
  /** Stable client-generated id. */
  id: string;
  /** Optional user-named label (e.g. "Stock setup", "After +1/4 turn limb"). */
  label?: string;
  /** Optional narrative notes for the set. */
  notes?: string;
  /** What changed since the previous set, in the user's words. */
  setupDelta?: string;
  /** ISO timestamp set started. */
  startedAt: string;
  /** ISO timestamp set ended. Open sets (the current one) have no endedAt. */
  endedAt?: string;
  /** References into the session's RichShot[].id list. Order = chronology. */
  shotIds: string[];
  // ─── v133 (Photo-Anchored Logging) additive extensions ───
  /** Base64 dataURI of the target photo for this set. Stored at the SET level
   *  (one photo covers a contiguous group of shots at one configuration).
   *  Typical compressed JPEG size: 200–500 KB. */
  photoDataUri?: string;
  /** 2-point calibration mapping the photo pixels to inches from POA. */
  photoCalibration?: PhotoCalibration;
}

/**
 * v133 — 2-point calibration for a target photo.
 *
 * Math: the user taps two reference points on the photo:
 *   1) `bullCenterPx` — the target's bull / POA → maps to (0,0) inches
 *   2) `calibPointPx` — a point at known physical distance from bull (e.g. the
 *       8-ring edge at 4.0" radius for a standard 40 cm face)
 *
 * The pixel distance between those two taps divided by `calibScoringRingIn`
 * gives a px-per-inch scale. Every subsequent tap on the photo is mapped from
 * `(px - bullCenterPx) / scale` to inches and stored as `RichShot.hitXIn/hitYIn`.
 *
 * Photo orientation: the user is responsible for taking the photo square to
 * the target and rotating it so vertical is up before calibrating. v133 ships
 * a linear axis-aligned scale only — rotation correction is a v134 candidate.
 */
export interface PhotoCalibration {
  /** Bullseye / POA tap location in photo pixel coordinates. */
  bullCenterPx: { x: number; y: number };
  /** Calibration reference tap location in photo pixel coordinates. */
  calibPointPx: { x: number; y: number };
  /** Physical inch distance from `bullCenterPx` to `calibPointPx`. Common
   *  values: 4.0 (standard 8-ring edge at 40 cm face), 3.0 (FITA blue ring),
   *  6.0 (full target face edge to bull at 40 cm). */
  calibScoringRingIn: number;
  /** Photo intrinsic dimensions (so we can re-render the calibration overlay
   *  at the original aspect ratio when the user reopens the set later). */
  photoWidthPx: number;
  photoHeightPx: number;
}

/** v132 extensions to ShotSession. All optional for backward compatibility. */
export interface SessionV3Extensions {
  /** Ordered list of sets. The last set is the open one if it has no endedAt.
   *  When undefined OR empty, the session is treated as a single implicit set. */
  sets?: ShotSet[];
  /** Schema version stamp. v3 sessions write this; older sessions don't. */
  schemaVersion?: 3;
}

/** Capture mode preference — persisted in localStorage. */
export type CaptureMode = 'quick' | 'advanced';

/** Display unit preference — persisted in localStorage.
 *  Canonical storage in the data model stays in INCHES regardless. */
export type DisplayUnit = 'in' | 'cm';

/** Storage keys touched by v132. Single source of truth for grep-ability. */
export const V132_KEYS = {
  activeSessionV3: 'sparrow.rangeLog.activeSession.v3',
  activeSessionV2: 'sparrow.rangeLog.activeSession.v2', // read fallback
  captureMode: 'sparrow.rangeLog.captureMode',
  displayUnit: 'sparrow.rangeLog.unit',
} as const;

/** Compare-overlay color palette. Cycles in order for set/session sources.
 *  Hex literals match Tailwind 500-band ember/cyan/violet/teal/etc. */
export const OVERLAY_PALETTE: readonly string[] = [
  '#22d3ee', // cyan-400
  '#f59e0b', // amber-500 (ember-tier)
  '#8b5cf6', // violet-500
  '#14b8a6', // teal-500
  '#ec4899', // pink-500 / magenta
  '#eab308', // yellow-500 / gold
  '#0ea5e9', // sky-500
  '#f43f5e', // rose-500
] as const;

/** Pick a stable color for an arbitrary source id by hashing into the palette. */
export function colorForSource(sourceId: string, palette: readonly string[] = OVERLAY_PALETTE): string {
  let h = 0;
  for (let i = 0; i < sourceId.length; i++) h = (h * 31 + sourceId.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

/** Aggregate stats computed for a single set or comparison source. */
export interface SetAggregates {
  count: number;
  meanRadiusIn: number;
  group95RadiusIn: number;
  centerX: number;
  centerY: number;
  bestRadiusIn: number;
  worstRadiusIn: number;
}

/** One row in the Compare Overlay legend. */
export interface OverlayLegendRow {
  /** Source identifier — set id, session id, or compound source. */
  sourceId: string;
  /** Display label (set label, session location/date, etc.). */
  label: string;
  /** Color assigned for this source's shots. */
  color: string;
  /** Number of shots from this source. */
  shotCount: number;
  /** Aggregate stats for this source's shots. */
  aggregates: SetAggregates | null;
}

/** Unit conversion — canonical storage is inches.
 *  1 in = 2.54 cm exactly. Round-trip lossless to ~14 significant digits. */
export function inToCm(inches: number): number {
  return inches * 2.54;
}
export function cmToIn(cm: number): number {
  return cm / 2.54;
}

/** Format a hit offset for display. Sign indicates direction; suffix indicates unit + R/L/U/D. */
export function formatOffsetDisplay(
  hitXIn: number,
  hitYIn: number,
  unit: DisplayUnit,
): { x: string; y: string; full: string } {
  const xVal = unit === 'cm' ? inToCm(hitXIn) : hitXIn;
  const yVal = unit === 'cm' ? inToCm(hitYIn) : hitYIn;
  const u = unit === 'cm' ? 'mm' : '"';
  const factor = unit === 'cm' ? 10 : 1; // mm-precision when cm chosen
  const xMag = Math.abs(xVal) * factor;
  const yMag = Math.abs(yVal) * factor;
  const xDir = hitXIn >= 0 ? 'R' : 'L';
  const yDir = hitYIn >= 0 ? 'DN' : 'UP';
  const xStr = `${xMag.toFixed(unit === 'cm' ? 0 : 2)}${unit === 'cm' ? 'mm' : u} ${xDir}`;
  const yStr = `${yMag.toFixed(unit === 'cm' ? 0 : 2)}${unit === 'cm' ? 'mm' : u} ${yDir}`;
  void u;
  return { x: xStr, y: yStr, full: `${xStr}  ·  ${yStr}` };
}

// Re-export RichShot for convenience so v3 consumers don't need to import from v2-schema
export type { RichShot };
